import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Button,
  Grid,
  Typography,
  Card,
  CardActions,
  CardMedia,
  CardContent,
  Chip,
  Fab,
  Icon
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { imgBaseUrl } from 'configs';
import { BackdropCircular } from 'components';
import { saveStoreProductMediaGalleryApi } from 'api';
import {
  uploadFileTypeUnsupported,
  uploadFileSizeLimit,
  uploadFileNameUnsupportedSymbols
} from 'redux/actions/general';
import {fileToBase64} from '../../utils';
import ProductImageEditor from './productImageEditor';


const useStyles = makeStyles({
  media: {
    height: '200px'
  },
  disabled: {
    filter: 'brightness(0.6) opacity(0.7)',
    textShadow: '1px 1px #000'
  }
});

const imageRoles = {
  image: 'Base',
  small_image: 'Small',
  thumbnail: 'Thumbnail'
};

const ProductImageGallery = ({product}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [mediaData, setMediaData] = useState({});
  const [openImageEditor, toggleOpenImageEditor] = useState(false);

  const {id, sku, media_gallery_entries} = product;
  const { stores = [] } = useSelector((state) => state.user.profile);
  const { savingProductMediaGallery } = useSelector((state) => state.products);
  const mediaStoreUrl = `${stores[0]?.apiUrl}/media/catalog/product` || imgBaseUrl;

  const handleDeleteMedia = (mediaId) => {
    const productToUpdate = {
      id,
      sku,
      media_gallery_entries: product.media_gallery_entries.filter((media) => media.id !== mediaId)
    };
    saveStoreProductMediaGalleryApi({dispatch, product: productToUpdate});
  };

  const handleSaveImage = async (event) => {
    const {files} = event.target;
    const listOfFiles = [...files];
    const allowedMimeTypes = ['image/jpeg', 'image/png'];
    const filesReadyToSending = [];

    const promisses = listOfFiles.map(async (el, idx) => {
      if (!allowedMimeTypes.includes(el?.type)) {
        event.target.value = '';
        dispatch(uploadFileTypeUnsupported());
      } else if (el.size > 250 * 1024) {
        dispatch(uploadFileSizeLimit('250kB'));
      } else if (el.name.match(/[\^\\/?*:"';<>()|{}\\\\]/g)) {
        event.target.value = '';
        dispatch(uploadFileNameUnsupportedSymbols(el.name));
      } else {
        const file = await fileToBase64(el);
        const base64Image = btoa(file);
        const [name] = el.name.split('.');

        filesReadyToSending.push(
          {
            label: name,
            disabled: false,
            media_type: 'image',
            position: media_gallery_entries.length + (idx + 1),
            types: !media_gallery_entries.length ? ['image', 'small_image', 'thumbnail'] : [],
            content: {
              base64_encoded_data: base64Image,
              type: el.type,
              name
            }
          }
        );
      }
    });

    await Promise.all(promisses);

    const productToUpdate = {
      id,
      sku,
      media_gallery_entries: [
        ...media_gallery_entries,
        ...filesReadyToSending
      ]
    };

    if (filesReadyToSending.length) {
      saveStoreProductMediaGalleryApi({dispatch, product: productToUpdate});
    }
  };

  return (
    <div className="m-4 w-full">
      {!media_gallery_entries.length ?
        <Card elevation={10} className="flex-column h-full items-center justify-center p-10">
          <div>
            <Icon className="pr-8 text-48" fontSize="large" color="primary">cloud_upload</Icon>
          </div>
          <span className="font-bold text-16 text-brand">Upload New Image</span>
          <span className="text-13 text-muted">Files supported: JPG and PNG</span>
          <p className="text-13 text-brand my-2">or</p>
          <label htmlFor="upload-image">
            <Fab
              component="span"
              variant="extended"
              className="border-radius-30 capitalize bg-white text-13 h-30px text-primary font-bold border-1-primary box-shadow-none"
            >
              Browse file
            </Fab>
          </label>
          <input
            className="hidden"
            onChange={handleSaveImage}
            id="upload-image"
            type="file"
            accept="image/png, image/jpeg"
            multiple
          />
        </Card>
        :
        <>
          <Grid container spacing={3}>
            {media_gallery_entries.map((media, idx) => {
              const {id, file, label, types, disabled} = media;
              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={`image-${idx}`}>
                  <Card elevation={10} className="h-full">
                    <CardMedia
                      image={`${mediaStoreUrl}${file}`}
                      title={label}
                      className={`${classes.media} ${disabled && classes.disabled} flex`}
                    >
                      {disabled &&
                        <div className={'m-auto text-36 uppercase text-white'}>
                          Hidden
                        </div>
                      }
                    </CardMedia>
                    <CardContent>
                      <Typography gutterBottom variant="h5" component="h2">
                        {label}
                      </Typography>
                      <div className="min-h-30 mt-2">
                        {!!types.length && types.map((type) => (
                          <Chip
                            key={`label-type-${type}`}
                            label={imageRoles[type]}
                            color="primary"
                            className="mr-2 mb-2"
                          />
                        ))
                        }
                      </div>
                    </CardContent>
                    <CardActions>
                      <Button
                        size="small"
                        variant="outlined"
                        fullWidth
                        onClick={() => handleDeleteMedia(id)}
                      >
                        Delete
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={() => {
                          setMediaData(media);
                          toggleOpenImageEditor(true);
                        }}
                      >
                        Edit
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Card elevation={10} className="flex-column h-full items-center justify-center p-10 text-center">
                <div>
                  <Icon className="pr-8 text-48" fontSize="large" color="primary">cloud_upload</Icon>
                </div>
                <div className="font-bold text-16 text-brand">Add New Image</div>
                <div className="text-13 text-muted">Files supported: JPEG and PNG</div>
                <div className="text-13 text-muted">File size should be less than 250kB</div>
                <div className="text-13 text-muted mb-4">Recommended resolution: 1000x1000px or less. Aspect ratio 1:1 is preferred.</div>
                <label htmlFor="upload-image">
                  <Fab
                    component="span"
                    variant="extended"
                    className="border-radius-30 capitalize bg-white text-13 h-30px text-primary font-bold border-1-primary box-shadow-none"
                  >
                    Browse file
                  </Fab>
                </label>
                <input
                  className="hidden"
                  onChange={handleSaveImage}
                  id="upload-image"
                  type="file"
                  accept="image/png, image/jpeg"
                  multiple
                />
              </Card>
            </Grid>
          </Grid>
        </>
      }
      {mediaData &&
        <ProductImageEditor
          product={product}
          media={mediaData}
          open={openImageEditor}
          isQuote={false}
          setOpen={(close) => {
            toggleOpenImageEditor(close);
            setMediaData({});
          }}
        />
      }
      <BackdropCircular loading={savingProductMediaGallery} />
    </div>
  );
};

export default ProductImageGallery;
